/**
 * WhatsApp delivery contracts — slice 12, "what happened to the WhatsApp
 * message".
 *
 * Slice 7 could submit a template and send from it. What it could not do was
 * learn anything afterwards: the Graph API returns a `wamid` and Wend wrote
 * `messages.status = 'sent'`, and **that status never moved again**. Meta's
 * webhook was already built and already verified — `meta-webhooks.controller.ts`
 * checks `X-Hub-Signature-256` over the raw bytes before a single field is read
 * — but it handles `message_template_status_update` and nothing else, so a
 * WhatsApp message's entire life after acceptance was invisible. This file is
 * the vocabulary of the half that was missing, and the one place that says what
 * each of Meta's five words means for a credit, a status and a suppression.
 *
 * ## Why this is a new file rather than more of `delivery.ts`
 *
 * `delivery.ts` is where the SES vocabulary lives, and the first instinct — put
 * the Meta vocabulary beside it, one delivery file — was tried and abandoned for
 * a mechanical reason, not a stylistic one.
 *
 * {@link WaReadRate} needs {@link import('./overview.js').rateReading}. And
 * `./overview.js` **imports `./delivery.js`** — slice 10 put
 * `notDeliveredBreakdownSchema` in `delivery.ts` precisely so the arrow would
 * point one way. Adding a `./overview.js` import to `delivery.ts` closes that
 * arrow into a cycle between two modules that both build zod schemas at load
 * time, and that kind of cycle **fails at import, not at review**: one of the
 * two modules is evaluated with the other's exports still `undefined`, and
 * `z.enum(undefined)` throws before a single test runs. Slice 10 hit this and
 * wrote it down; this file is what following that note looks like.
 *
 * `./whatsapp.js` was the other candidate and is worse. It already sits in a
 * cycle with `./template.js` (each imports the other), and dragging
 * `./overview.js` — and through it `./delivery.js`, `./credit.js`,
 * `./campaign.js`, `./integration.js` — into that cycle's evaluation order would
 * be betting the whole package's load on module ordering. `./whatsapp.js` is
 * also the *authoring* file: what a person composes and submits to Meta. This is
 * the *reported* file: what Meta says back about a message already gone. They
 * are different directions of the same integration and they fail differently.
 *
 * So the arrows are:
 *
 * ```
 *   whatsapp-delivery → overview → delivery → credit
 *                     ↘ whatsapp (the webhook envelope, parsed once)
 * ```
 *
 * and **nothing imports this file**. It is a leaf, exported last from
 * `../index.js`, which is what keeps the property cheap to keep.
 *
 * ## The five things this file is built around
 *
 * **1. There is no bounce.** Meta reports `sent`, `delivered`, `read`, `failed`
 * and `deleted`. A `failed` carries an error code, and the code is the entire
 * diagnosis: an unregistered number, a policy block, a rate limit and an expired
 * 24-hour window all arrive as `failed` and mean four completely different
 * things. {@link WA_FAILURE_CLASS_BY_CODE} is the branch, and it is the
 * WhatsApp answer to {@link import('./delivery.js').isHardBounce} — with the
 * same asymmetry, argued at length there: **failing to suppress costs a wasted
 * message; wrongly suppressing costs a real customer, permanently.** `suppressions`
 * is append-only and grants the application role neither UPDATE nor DELETE.
 * Exactly one class may suppress; see {@link WA_SUPPRESSING_FAILURE_CLASSES},
 * which is derived from the table rather than written beside it.
 *
 * **2. `read` is a floor, not a rate.** A recipient who turns read receipts off
 * is never reported as having read anything, however carefully they read it.
 * That is not a caveat that can be reworded away, so it is a property of the
 * *type*: {@link WaReadRate} carries `confidence: 'floor'` and a non-empty
 * caveat list, and is mutually non-assignable to both
 * {@link import('./overview.js').OverviewRateKpi} (which has `threshold` and
 * `band`, and this has neither) and
 * {@link import('./engagement.js').EngagementRate} (whose `confidence` is the
 * literal `'directional'`, not `'floor'`). Slice 11 established that precedent
 * for the open rate; a WhatsApp read figure is if anything more misleading, so
 * it gets the same treatment rather than a sentence in the copy.
 *
 * **3. Link clicks are not in this webhook, and are not designed out.** Meta
 * reports template button clicks through Template Analytics, aggregated per
 * template per day, with no recipient identity — the same shape as VDM, and
 * therefore the same rule slice 11 wrote for VDM: a panel, never a source of
 * truth, never joined to a contact. Nothing here has a `clicked` member, and
 * nothing here would have to change to add that panel later, because it would
 * not be a per-message event and would not live in `delivery_events` at all.
 *
 * **4. The credit rule is stated, not assumed.** See
 * {@link WA_CREDIT_EFFECT_BY_STATUS}, which defends every row including the
 * boring ones.
 *
 * **5. Statuses arrive out of order and more than once.** Meta gives no
 * ordering guarantee and it retries. A `read` can arrive before its `delivered`;
 * any status can be redelivered. Two existing guards carry that, unchanged and
 * deliberately not reimplemented here —
 * {@link import('./delivery.js').isCreditEffectApplicable} (a settled message
 * takes no further credit movement) and `credit_ledger.idempotency_key` (the
 * same movement never twice) — and one new one is this file's:
 * {@link waProviderEventId}, because Meta's status objects **share the
 * message's `wamid` and carry no event id of their own**, so the naive dedupe
 * key would make `delivered` look like a replay of `sent` and drop it silently.
 *
 * ## No migration, and that was checked rather than assumed
 *
 * `delivery_events.event_type` is `text`, not an enum, and 0011's header says
 * why in as many words. `provider` is `integration_kind`, which already has
 * `meta_whatsapp`. `period` is pinned to `date_trunc('month', occurred_at)` by a
 * CHECK, and the replay guard `UNIQUE (provider, provider_event_id, period)`
 * already contains the partition key. Every shape here is storable today.
 *
 * One collision was checked and is clear: folded to lower-case,
 * {@link WA_MESSAGE_STATUS_VALUES} and
 * {@link import('./delivery.js').SES_EVENT_TYPE_VALUES} are **disjoint** —
 * `sent`/`send` and `delivered`/`delivery` are near misses and nothing more. So
 * `parseSesEventType` returns `null` for every WhatsApp row, and the two SES
 * readers that scan `delivery_events` without a `provider` predicate
 * (`notDeliveredBreakdown`, `bounceBreakdown`) ignore WhatsApp rows structurally
 * rather than by remembering to filter. That is worth an assertion in a
 * verification and it is why neither vocabulary may ever gain the other's word.
 */
import { z } from 'zod';

import { isoDateTimeSchema, uuidSchema } from '../common.js';
import type { MessageStatus, RecipientStatus, SuppressionReason } from '../enums.js';
import type { CreditEffect } from './credit.js';
import { campaignResultsResponseSchema, type DeliveryOutcome } from './delivery.js';
import { deliveryReportsSchema } from './delivery-reports.js';
import { rateReading, rateReadingSchema } from './overview.js';
import { metaWebhookPayloadSchema, type MetaWebhookPayload } from './whatsapp.js';

/* -------------------------------------------------------------------------- */
/* Wire values — one normalisation, the same one used three files over         */
/* -------------------------------------------------------------------------- */

/**
 * Folds a provider's spelling down to something matchable.
 *
 * The third copy of this three-line function in the package (`delivery.ts` and
 * `whatsapp.ts` have the others) and deliberately not exported from any of them.
 * It is three lines of normalisation, not a rule: the thing worth sharing is the
 * *table* a fold is applied to, and every one of those is a single exported
 * constant. A shared `foldWireValue` would be a fourth import arrow between
 * files whose import graph this file's own header is about.
 *
 * Checked here as it was checked there: no two members of either vocabulary
 * below collide under this fold.
 */
function foldWireValue(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

/* -------------------------------------------------------------------------- */
/* Meta's message status vocabulary                                            */
/* -------------------------------------------------------------------------- */

/**
 * Every per-message status Meta's `messages` webhook is documented to report.
 *
 * The set is closed and it is **Meta's, not Wend's**. Carrying only the three
 * that move something would mean the others arrive, fail to parse, and become
 * indistinguishable from a status that never came — the failure mode
 * `SES_EVENT_TYPE_VALUES` names, and the expensive half of it is that an
 * unrecognised event looks exactly like an outage in the webhook.
 *
 *   - `sent` — Meta accepted it and put it on the wire. The analogue of SES's
 *     `Send`, including its job of reconciling a `messages.status` of `unknown`;
 *   - `delivered` — it reached the recipient's device. **The only status that
 *     consumes a credit**;
 *   - `read` — the recipient opened the chat. A floor and not a rate; see
 *     {@link WaReadRate};
 *   - `failed` — it did not arrive. The error code is the entire diagnosis;
 *     see {@link WA_FAILURE_CLASS_BY_CODE};
 *   - `deleted` — the *business* deleted a message it had already sent. A
 *     statement about what the recipient can still see, not about whether it
 *     ever arrived, and therefore inert here;
 *   - `warning` — Meta documents this on the status object for some interactive
 *     and order messages. It is carried so it parses and is stored, and it is
 *     inert in every table below. **Inert is the honest position for a status
 *     whose meaning we have not confirmed**, and it is cheap: the raw payload is
 *     in `webhook_events` either way.
 *
 * Written lower-case because this value goes into `delivery_events.event_type`,
 * which carries `CHECK (event_type = lower(event_type))`. Meta already sends
 * them lower-case; {@link WA_MESSAGE_STATUS_BY_WIRE_VALUE} is the only place
 * that is assumed rather than hoped.
 *
 * Splitting these members from {@link WA_CREDIT_EFFECT_BY_STATUS} is how a
 * member gets added without anybody deciding what it does to a tenant's
 * balance, so the two are next to each other and the effect table is total over
 * this tuple.
 */
export const WA_MESSAGE_STATUS_VALUES = [
  'sent',
  'delivered',
  'read',
  'failed',
  'deleted',
  'warning',
] as const;
export const waMessageStatusSchema = z.enum(WA_MESSAGE_STATUS_VALUES);
export type WaMessageStatus = z.infer<typeof waMessageStatusSchema>;

/**
 * Meta's documented spellings. **The only place they appear**, exactly as
 * `SES_EVENT_TYPE_BY_WIRE_VALUE` is for AWS's.
 *
 * They are already lower snake_case on the wire, so this table looks redundant
 * and is not: it is the single edit point on the day Meta ships a status spelled
 * `Read` or `READ` in one API version and `read` in another, which is precisely
 * what it did to the *template* status vocabulary
 * (`PENDING_DELETION` / `Pending deletion`) one file over.
 */
export const WA_MESSAGE_STATUS_BY_WIRE_VALUE: Readonly<Record<string, WaMessageStatus>> = {
  sent: 'sent',
  delivered: 'delivered',
  read: 'read',
  failed: 'failed',
  deleted: 'deleted',
  warning: 'warning',
};

const FOLDED_WA_MESSAGE_STATUS = new Map<string, WaMessageStatus>(
  Object.entries(WA_MESSAGE_STATUS_BY_WIRE_VALUE).map(([wire, member]) => [
    foldWireValue(wire),
    member,
  ]),
);

/**
 * Meta's word → ours, or `null` for a word we do not know.
 *
 * **Returns `null` rather than throwing.** Meta has added members to this
 * webhook before, and a parser that threw would turn a new status into a 500,
 * a retry storm, and eventually a webhook Meta stops calling — at which point
 * every status for every tenant stops arriving because of one word. The caller
 * stores the raw payload and acts on nothing.
 */
export function parseWaMessageStatus(raw: unknown): WaMessageStatus | null {
  return typeof raw === 'string' ? (FOLDED_WA_MESSAGE_STATUS.get(foldWireValue(raw)) ?? null) : null;
}

/* -------------------------------------------------------------------------- */
/* Failure classification — the one that burns a list if it is wrong           */
/* -------------------------------------------------------------------------- */

/**
 * What a `failed` status actually means, once its error code is read.
 *
 * This is the WhatsApp counterpart of `SesBounceType`, and it needs more than
 * three members because Meta's `failed` is doing the work of SES's `Bounce`,
 * `Reject`, `RenderingFailure` **and** a rate limiter, all under one word.
 * Splitting them is not presentation: the five classes differ in *who has to do
 * something* and *what that something is*, which is the same argument
 * `notDeliveredBreakdownSchema` makes for keeping its five outcomes apart.
 *
 *   - `unreachable` — this number will never receive a WhatsApp message. **The
 *     only class that may suppress**, and the only one where doing nothing costs
 *     a wasted message on every future campaign forever;
 *   - `transient` — it did not work *this time*. Rate limits, Meta capacity,
 *     maintenance. The number is fine and the campaign can be resent. **A rate
 *     limit must never permanently suppress a real customer**, which is the
 *     single sentence this whole table exists to make structurally true;
 *   - `policy` — Meta refused to deliver *this message* for a messaging-policy
 *     reason about this conversation: the 24-hour customer-service window has
 *     closed, a per-user marketing frequency cap was hit, the business tripped a
 *     spam limit. The recipient is reachable. The tenant fixes this by changing
 *     what they send or when;
 *   - `template` — the template is the problem: paused, disabled, missing, or
 *     handed the wrong number of parameters. The tenant fixes this **inside
 *     Wend**, which is what makes it worth its own class rather than being filed
 *     under `policy`;
 *   - `account` — the WABA, the phone number or the billing is broken, and
 *     **every** message will fail until somebody fixes it. Operationally this is
 *     a banner and a stopped campaign, not a line in a per-message breakdown;
 *   - `request` — Wend built a request Meta refused. Ours, and the only class
 *     that is this product's own fault. It is here for the same reason
 *     `renderingFailed` is in `notDeliveredBreakdownSchema`: hiding our failures
 *     inside the tenant's makes them unfixable;
 *   - `unclassified` — a code that is not in the table, or a `failed` that
 *     carried no code at all. **Never suppresses**, by construction rather than
 *     by remembering to; see {@link classifyWaFailureCode}.
 */
export const WA_FAILURE_CLASS_VALUES = [
  'unreachable',
  'transient',
  'policy',
  'template',
  'account',
  'request',
  'unclassified',
] as const;
export const waFailureClassSchema = z.enum(WA_FAILURE_CLASS_VALUES);
export type WaFailureClass = z.infer<typeof waFailureClassSchema>;

/**
 * **Meta's Cloud API error codes, classified.** The table the suppression
 * decision is taken from, and the most dangerous object in this file.
 *
 * Two rules were applied to every row, and both are asymmetric on purpose:
 *
 *   1. **A code goes in `unreachable` only if its documented meaning is "this
 *      number cannot receive a WhatsApp message".** Not "did not", not "is not
 *      allowed to right now" — cannot;
 *   2. **Where the documented meaning is ambiguous, the code is classified
 *      NON-suppressing and the ambiguity is written down in a comment rather
 *      than resolved silently.** Failing to suppress costs a wasted message,
 *      visible in the failure breakdown and repaired by the next campaign.
 *      Wrongly suppressing is repaired by nothing: `suppressions` is append-only
 *      by design, the application role has neither UPDATE nor DELETE on it, and
 *      the customer is a cooperative bank that stops hearing from its own summit
 *      organiser and never finds out why.
 *
 * Codes not in this table classify as `unclassified` and suppress nobody, so
 * **the failure mode of an out-of-date table is a wasted send, never a lost
 * customer.** That is the whole reason the default is a member of the enum
 * rather than a `null` somebody has to remember to branch on.
 */
export const WA_FAILURE_CLASS_BY_CODE: Readonly<Record<number, WaFailureClass>> = {
  /* ---- unreachable: the only class that may suppress ------------------- */

  /**
   * 131026 — "Message undeliverable". The nearest thing WhatsApp has to a hard
   * bounce, and the only code in this table that suppresses.
   *
   * Meta documents several causes under it, and they are honestly not all
   * permanent: the number is not a WhatsApp number; the recipient has not
   * accepted Meta's current terms; the recipient is on a WhatsApp version too
   * old to receive the message. It is classified `unreachable` anyway, which is
   * the one place this table resolves an ambiguity *towards* suppressing, so the
   * reasoning had better be here:
   *
   *   - the dominant real-world cause, by a wide margin, is a number that is not
   *     on WhatsApp at all — a landline, a mistyped mobile, a bank's switchboard
   *     in a spreadsheet column. It is never going to work;
   *   - the other two causes are states **the recipient must change on their own
   *     device**. Nothing Wend does — resending, waiting, resending again —
   *     moves them, so retrying is not a repair, it is a slower way to fail;
   *   - there is no other code that means "not a WhatsApp user". If this one
   *     does not suppress, nothing does, and a list of landlines is retried on
   *     every campaign forever, burning credits and dragging the tenant's Meta
   *     quality rating down — which under BYO lands on *their* WABA.
   *
   * A recipient in one of the two recoverable states who later fixes it is
   * re-added by the tenant, deliberately and by hand, which is the same repair
   * path an SES hard bounce has.
   */
  131026: 'unreachable',

  /* ---- transient: never suppresses ------------------------------------- */

  /** 130429 — Cloud API message throughput limit. A capacity fact about us. */
  130429: 'transient',
  /** 131000 — "Something went wrong". Meta's own internal failure. */
  131000: 'transient',
  /** 131016 — Service unavailable. */
  131016: 'transient',
  /**
   * 131056 — business/consumer pair rate limit: too many messages between this
   * sender and this recipient in a short window. **The code most likely to be
   * mistaken for a hard failure**, because it is per-recipient and so looks like
   * a fact about the number. It is a fact about the pace.
   */
  131056: 'transient',
  /** 131057 — account in maintenance mode. Temporary by definition. */
  131057: 'transient',
  /** 132069 — flow throttled. */
  132069: 'transient',
  /** 133004 — server temporarily unavailable. */
  133004: 'transient',

  /* ---- policy: the tenant's to fix, and the recipient is fine ---------- */

  /**
   * 131047 — re-engagement message: more than 24 hours have passed since the
   * customer's last message, so a non-template message cannot be sent. The
   * recipient is perfectly reachable — with a template. Suppressing here would
   * permanently delete a live customer over a timing rule.
   */
  131047: 'policy',
  /** 131048 — spam rate limit hit. A quality signal about the sender. */
  131048: 'policy',
  /**
   * 131049 — Meta declined to deliver "to maintain healthy ecosystem
   * engagement": the per-user marketing frequency cap. Entirely about how much
   * marketing that person has already received this period, from everyone.
   */
  131049: 'policy',
  /**
   * 131050 — documented around marketing opt-out. **Deliberately NOT
   * suppressing, because its meaning is not confirmed.** If it does mean the
   * recipient has opted out of marketing, it deserves a suppression of its own
   * with reason `unsubscribe` — not `hard_bounce` — and that is a decision to
   * take against Meta's documentation open, not against a memory of it. Until
   * then it is a policy refusal that costs a message and nothing else, which is
   * the cheap way to be wrong.
   */
  131050: 'policy',
  /**
   * 130472 — the recipient's number is part of a Meta experiment and the message
   * was withheld. Meta chose not to deliver it; the number is fine.
   */
  130472: 'policy',

  /* ---- template: the tenant's to fix, inside Wend ---------------------- */

  /** 132000 — template parameter count mismatch. */
  132000: 'template',
  /** 132001 — template does not exist (name/language pair Meta does not have). */
  132001: 'template',
  /** 132005 — hydrated template text too long. */
  132005: 'template',
  /** 132007 — template content violates a format or character policy. */
  132007: 'template',
  /** 132012 — template parameter format mismatch. */
  132012: 'template',
  /**
   * 132018 — invalid parameter on a template send, and **new to this product
   * with slice 51**. Meta added it "for help diagnosing invalid parameters when
   * attempting to send a template message" and says it "applies to version 23.0
   * and newer versions" (WhatsApp Business Platform changelog, 29 May 2025),
   * so while these calls were pinned to v21.0 it could not arrive at all. At
   * v25.0 it can.
   *
   * `template`, beside its siblings, because it is the tenant's to fix inside
   * Wend and says nothing about the recipient: the commonest way to produce it
   * is a binding whose value is not what the position will take, which is
   * slice 49's Personalisation panel. Left unmapped it would have fallen to the
   * catch-all and told a sender to try again — advice that cannot work, because
   * the next attempt carries the same parameter.
   */
  132018: 'template',
  /**
   * 132015 / 132016 — the template is paused or disabled by Meta. `whatsapp.ts`
   * already folds both towards unsendable in `TEMPLATE_STATUS_BY_META_STATUS`;
   * this is the same fact arriving through the send path instead of the template
   * webhook, and the two must agree.
   */
  132015: 'template',
  132016: 'template',
  /** 132068 — flow is blocked. */
  132068: 'template',

  /* ---- account: everything fails until somebody fixes the WABA --------- */

  /** 0 — AuthException. The credential is not usable. */
  0: 'account',
  /** 3 — API method unavailable to this app. */
  3: 'account',
  /** 10 — permission denied. */
  10: 'account',
  /** 33 — object does not exist, or this token cannot see it. */
  33: 'account',
  /**
   * 190 — access token expired. Named explicitly because it is the failure this
   * workspace already contains: an integration whose Meta token has expired, and
   * whose correct behaviour is a loud banner and a stopped campaign — never a
   * suppression, because not one recipient did anything wrong.
   */
  190: 'account',
  /** 131005 — access denied. */
  131005: 'account',
  /** 131031 — business account locked or restricted. */
  131031: 'account',
  /** 131042 — business eligibility / payment method problem. */
  131042: 'account',
  /** 131045 — number not registered correctly (certificate). */
  131045: 'account',
  /** 133010 — phone number not registered. */
  133010: 'account',
  /** 133015 — phone number is being deregistered. */
  133015: 'account',

  /* ---- request: Wend built something Meta refused ---------------------- */

  /** 100 — invalid parameter. */
  100: 'request',
  /** 131008 — a required parameter is missing. */
  131008: 'request',
  /** 131009 — a parameter value is not valid. */
  131009: 'request',
  /**
   * 131021 — recipient cannot be sender: the message was addressed to the
   * business's own number. It will never work, and it is still not
   * `unreachable`: it is a configuration mistake, and filing it under the class
   * that suppresses would put a tenant's own number on their suppression list
   * for a reason that has nothing to do with a recipient.
   */
  131021: 'request',
  /** 131051 — unsupported message type. */
  131051: 'request',
  /** 131052 — media download error (the URL we gave Meta). */
  131052: 'request',
  /** 131053 — media upload error. */
  131053: 'request',
  /**
   * 135000 — "generic user error". Meta's own catch-all for a request it
   * refused without saying why. Filed here rather than in `unclassified` because
   * it *is* a documented code, and non-suppressing either way — which is the
   * only property that matters for a code nobody can explain.
   */
  135000: 'request',
};

/**
 * A `failed` status's error code → what it means. **Total, and never `null`.**
 *
 * An absent code, a non-numeric code and a code the table does not carry all
 * answer `unclassified`, which suppresses nobody. There is no branch a caller
 * can forget, because there is no `null` to forget to handle.
 *
 * Accepts a string as well as a number because Meta has sent the code as both
 * across API versions, and a `'131026'` that silently failed to match would be
 * a suppression that stopped happening with no error anywhere.
 */
export function classifyWaFailureCode(code: number | string | null | undefined): WaFailureClass {
  if (code === null || code === undefined) return 'unclassified';
  const numeric = typeof code === 'number' ? code : Number.parseInt(code.trim(), 10);
  if (!Number.isFinite(numeric)) return 'unclassified';
  return WA_FAILURE_CLASS_BY_CODE[numeric] ?? 'unclassified';
}

/**
 * The failure classes that may write a suppression. **Derived, never written
 * out.**
 *
 * It has one member, and it has one member as a *consequence* of
 * {@link maySuppressWaFailure} rather than as a claim standing next to it —
 * the same construction `CONSUMING_EVENT_TYPES` uses in `delivery.ts`, for the
 * same reason: a verification asserts its length, which is the cheapest possible
 * guard on the most expensive possible mistake.
 */
export const WA_SUPPRESSING_FAILURE_CLASSES: readonly WaFailureClass[] =
  WA_FAILURE_CLASS_VALUES.filter((failureClass) => maySuppressWaFailure(failureClass));

/**
 * **The permanent/temporary rule.** `unreachable` suppresses; nothing else does.
 *
 * One expression, one place, fails closed for every class that is not the one.
 * Written as an equality rather than as a list of exclusions so that a class
 * added later is non-suppressing until somebody edits this line on purpose.
 */
export function maySuppressWaFailure(failureClass: WaFailureClass): boolean {
  return failureClass === 'unreachable';
}

/**
 * What a human is told about each class, where the number is read.
 *
 * These are not disclaimers, they are repairs: each sentence says who has to do
 * something and what. `transient` is worded to actively discourage list
 * cleaning, because "failed" on a screen is what makes somebody delete a good
 * number.
 */
export const WA_FAILURE_CLASS_MESSAGES: Readonly<Record<WaFailureClass, string>> = {
  unreachable:
    'These numbers cannot receive WhatsApp messages — most often because they are not on WhatsApp at all. They have been suppressed for this channel and will not be messaged again.',
  transient:
    'These did not go through this time — a rate limit, or WhatsApp being briefly unavailable. The numbers are fine. Do not clean them off the list; send the campaign again later.',
  policy:
    "WhatsApp declined to deliver these under its messaging rules — an expired 24-hour window, or a cap on how much marketing that person has already received. The numbers are reachable; what was sent, or when, is what needs to change.",
  template:
    'The template was refused at send: paused, disabled, missing, or given the wrong parameters. Fix the template in Wend and send again.',
  account:
    'Your WhatsApp Business account, number or billing is blocking every message — not these recipients. Nothing will send until it is fixed.',
  request:
    'Wend built a request WhatsApp refused. This one is ours, not yours or the recipient’s.',
  unclassified:
    'WhatsApp gave a reason Wend does not recognise. Nobody has been suppressed. The full error is stored with the event.',
};

/* -------------------------------------------------------------------------- */
/* THE MAPPING — what a status does to the ledger                              */
/* -------------------------------------------------------------------------- */

/**
 * **What each status does to the ledger.** Total over
 * {@link WaMessageStatus}, so a new status cannot be added without deciding
 * this.
 *
 * ```
 *   sent        null      the reservation already exists — see below
 *   delivered   consume   ← the only one
 *   read        null      ← the interesting one
 *   failed      release   it did not arrive; the reservation unwinds
 *   deleted     null      says nothing about whether it arrived
 *   warning     null      meaning unconfirmed; inert on purpose
 * ```
 *
 * **THE WHATSAPP CREDIT RULE, stated rather than inherited.** Slice 6 settled
 * email: *consume on delivery, release on bounce*. The WhatsApp analogue is
 * **consume on `delivered`, release on `failed`, and a `read` does nothing** —
 * and each of the three needs its own defence, because getting any of them
 * wrong drains or inflates a tenant's balance on every single message.
 *
 * **`delivered` consumes, and it is the only thing that does.** A credit is
 * Wend's price for *a message that arrived*, and `delivered` is the one status
 * that says a message arrived. Note what this does **not** key on: Meta's own
 * `pricing` rides along on the same status payload — and `conversation` did
 * until Meta's change of 8 October 2025, which removed it except at a Free
 * Entry Point. Both are the tenant's 24-hour-conversation billing with Meta
 * under BYO, and the argument below is unchanged by the removal: it is about
 * what Wend would do with the figure, not about whether Meta sends it.
 * They are deliberately ignored. Slice 7 wrote it down — *nothing derived from
 * a WhatsApp conversation may be rendered as a quantity, credits are Wend's
 * metering unit* — and keying a credit off Meta's billable flag would silently
 * make one Wend credit mean "a conversation" for WhatsApp and "a message" for
 * email, so a tenant's balance would fall at two different rates for the same
 * button.
 *
 * **`failed` releases, whatever the error code says.** Nothing arrived, so
 * there is nothing to charge for, and this is true identically for a dead
 * number, a rate limit and a paused template. The code decides the *suppression*
 * and nothing else — which is exactly the separation `delivery.ts` keeps between
 * `bounceType` (suppression) and the credit effect (always `release`), and it
 * has the same payoff: **the worst a misclassified error code can do is suppress
 * wrongly or fail to. It cannot corrupt the ledger.**
 *
 * **`read` is `null`, and this is the row most likely to be got wrong.** A read
 * is not a second delivery. The `delivered` that preceded it already consumed
 * the credit, so a `consume` here would charge twice for every message anybody
 * opened — and would try to decrement a `reserved` the delivery had already
 * taken to zero, which `credit_balances.reserved >= 0` turns into an aborted
 * transaction on a message that worked perfectly. It is `null` in the other
 * direction too: a read is not consent, so it may never produce a suppression
 * or reverse one.
 *
 * A `read` also moves **no status**, and that is the same argument
 * `deliveryOutcome` makes for a complaint: `MessageStatus` has no `read` member
 * and must not gain one, because adding it would overwrite `delivered` and with
 * it the record of why that message's credit is legitimately consumed. Reads are
 * counted separately — see {@link WaReadRate}.
 *
 * **The consequence of `read` being inert, named rather than discovered.** If
 * Meta drops a `delivered` and sends only the `read`, that message never
 * consumes its credit and its reservation sits until something resolves it. That
 * is a real hole and it is chosen over the alternative: letting `read` imply a
 * delivery gives the effect table two rows that can consume, and "exactly one
 * thing consumes" — {@link WA_CONSUMING_STATUSES}, derived — is the property
 * that makes the ledger auditable at all. Under-consuming is also the direction
 * that cannot be silently wrong in a tenant's favour or ours: the message is
 * visibly stuck at `sent` with a `read` event on file beside it, which is
 * exactly the shape a reconciliation job would look for. Building that job is
 * not this slice.
 *
 * **`sent` is `null` for a reason worth stating.** The reservation was written
 * in the same transaction as the `messages` row, before this status could exist,
 * so there is nothing left for it to do to the ledger. What it *does* do is
 * reconcile a `messages.status` of `unknown` — see {@link waDeliveryOutcome}.
 *
 * **`deleted` is `null`, and releasing there would be a real bug.** It means the
 * business retracted a message it had already sent. The message very probably
 * arrived and was read before it was pulled; releasing would hand back a credit
 * that was legitimately consumed and drive `reserved` negative on a message
 * whose delivery had already emptied it.
 */
export const WA_CREDIT_EFFECT_BY_STATUS: Readonly<Record<WaMessageStatus, CreditEffect>> = {
  sent: null,
  delivered: 'consume',
  read: null,
  failed: 'release',
  deleted: null,
  warning: null,
};

/**
 * The statuses that consume a credit. **Derived, never written out.**
 *
 * One member, as a consequence of the table above rather than as a claim beside
 * it. `delivery.ts` says the same thing about `CONSUMING_EVENT_TYPES` and the
 * two properties are asserted the same way.
 */
export const WA_CONSUMING_STATUSES: readonly WaMessageStatus[] = WA_MESSAGE_STATUS_VALUES.filter(
  (status) => WA_CREDIT_EFFECT_BY_STATUS[status] === 'consume',
);

/** The credit movement this status causes. A lookup, never a `switch`. */
export function creditEffectForWaStatus(status: WaMessageStatus): CreditEffect {
  return WA_CREDIT_EFFECT_BY_STATUS[status];
}

/* -------------------------------------------------------------------------- */
/* The outcome — credits, suppression and two statuses, from one payload       */
/* -------------------------------------------------------------------------- */

/** The fields of a parsed status that any decision here is allowed to read. */
export interface WaStatusFacts {
  readonly status: WaMessageStatus;
  /**
   * Present on a `failed`. `null` when Meta sent no `errors` array, which
   * happens, and which classifies as `unclassified` rather than throwing.
   *
   * A `string | number` because Meta has sent it as both across API versions.
   */
  readonly errorCode?: number | string | null;
}

/**
 * **The one place a WhatsApp status becomes a decision.**
 *
 * Returns the identical {@link import('./delivery.js').DeliveryOutcome} the SES
 * ingest applies, and that reuse is the point rather than a convenience: the two
 * ingests then write the same four things in the same order, and a reviewer
 * asking "what can a forged webhook move" reads one interface for both
 * providers. Inventing a `WaDeliveryOutcome` with the same four fields would
 * have been the moment the two paths started drifting.
 *
 * Note the shape of the failure branch. The credit is `release`
 * **unconditionally** — read straight from {@link WA_CREDIT_EFFECT_BY_STATUS},
 * not recomputed — while the suppression is the only thing the error code
 * touches. That separation is what makes a wrong entry in
 * {@link WA_FAILURE_CLASS_BY_CODE} a compliance mistake and never a ledger one.
 *
 * The suppression reason is `hard_bounce`, and the word is SES's rather than
 * Meta's. `suppression_reason` is one of CONTRACT.md's fixed enums, mirrored as
 * a pg enum, and a fifth member would be a migration this slice does not need:
 * `hard_bounce` already means *the provider says this address will never accept
 * a message*, which is precisely what 131026 says about a phone number. The
 * suppression row's `channel` is `whatsapp` and its `value` is the E.164 number
 * (0010's `suppressions_value_matches_channel` CHECK enforces the shape), so
 * nothing about an email address is ever confused with it.
 *
 * **Ordering is not this function's problem and must not become it.** It is a
 * pure reading of one payload; whether the movement it returns may still be
 * applied is `isCreditEffectApplicable`'s question, answered against the
 * message's current status, and it is imported rather than reimplemented. Two
 * copies of that rule is how a late `failed` after a `delivered` comes to drive
 * `reserved` negative in one code path and not the other.
 */
export function waDeliveryOutcome(facts: WaStatusFacts): DeliveryOutcome {
  const credit = creditEffectForWaStatus(facts.status);

  switch (facts.status) {
    case 'sent':
      // Reconciles a `messages.status` of `unknown`: Meta accepted it, and this
      // is the status that says so with Meta's own `wamid` attached.
      return { credit, suppression: null, messageStatus: 'sent', recipientStatus: 'sent' };

    case 'delivered':
      return { credit, suppression: null, messageStatus: 'delivered', recipientStatus: 'delivered' };

    case 'failed': {
      const failureClass = classifyWaFailureCode(facts.errorCode ?? null);
      return {
        credit,
        // The compliance spine, and the only branch that can permanently stop
        // messaging somebody. Exactly when the code means "never".
        suppression: maySuppressWaFailure(failureClass)
          ? ('hard_bounce' satisfies SuppressionReason)
          : null,
        // `failed`, never `bounced`. There is no bounce on WhatsApp, and writing
        // one would put rows with no `bounce.bounceType` in front of
        // `bounceBreakdown`, which would silently file every one of them under
        // `undetermined` — a wrong number that looks like a real measurement.
        messageStatus: 'failed' satisfies MessageStatus,
        recipientStatus: 'failed' satisfies RecipientStatus,
      };
    }

    case 'read':
    case 'deleted':
    case 'warning':
      // Recorded in `delivery_events` so a person can see it happened, and
      // touching nothing else. See WA_CREDIT_EFFECT_BY_STATUS for each.
      return { credit, suppression: null, messageStatus: null, recipientStatus: null };
  }
}

/**
 * Whether this status, on this code, is one the tenant should be told about at
 * all — used to decide whether a failure deserves a line on the screen.
 *
 * `sent`, `delivered` and `read` are not failures; `deleted` and `warning` are
 * not either. Exported so the campaign screen and the overview cannot each
 * decide for themselves whether a `deleted` counts as a problem.
 */
export function isWaFailure(status: WaMessageStatus): boolean {
  return status === 'failed';
}

/* -------------------------------------------------------------------------- */
/* The dedupe key Meta does not give us                                        */
/* -------------------------------------------------------------------------- */

/**
 * The separator inside a composed `delivery_events.provider_event_id`. A colon,
 * because Meta's `wamid` is base64url plus dots and never contains one.
 */
export const WA_PROVIDER_EVENT_ID_SEPARATOR = ':';

/**
 * **`delivery_events.provider_event_id` for a WhatsApp status.** Read this
 * before writing the ingest; the obvious alternative silently loses data.
 *
 * SES hands every notification its own SNS `MessageId`, so
 * `UNIQUE (provider, provider_event_id, period)` deduplicates replays for free.
 * **Meta gives a status object no id of its own.** Every status for one message
 * — `sent`, then `delivered`, then `read` — carries the *same* `wamid`. Using
 * the `wamid` as the provider event id would therefore make `delivered` look
 * like a replay of `sent` and the unique constraint would drop it, and the drop
 * is silent: the message would sit at `sent` for ever and the ingest would
 * report success. That is the bug this function exists to make unwriteable.
 *
 * So the key is `<wamid>:<status>`. It is stable across redeliveries of the same
 * status — which is what makes Meta's retries idempotent — and distinct across
 * different statuses of the same message, which is what makes the sequence
 * ingestible. The timestamp is deliberately **not** in it: including it would
 * make every redelivery a new row, and redelivery is the thing this key exists
 * to absorb.
 *
 * The residual weakening is 0011's and is unchanged: the unique index must
 * contain the partition key, so a status redelivered in a *different* UTC month
 * from the first inserts a second row. `credit_ledger.idempotency_key` is what
 * actually guarantees no credit moves twice — globally unique, unpartitioned —
 * and it must be keyed on this same string.
 */
export function waProviderEventId(messageId: string, status: WaMessageStatus): string {
  return `${messageId.trim()}${WA_PROVIDER_EVENT_ID_SEPARATOR}${status}`;
}

/* -------------------------------------------------------------------------- */
/* When it happened — unix seconds, and the off-by-1000 that follows           */
/* -------------------------------------------------------------------------- */

/**
 * The narrowest range a status timestamp is allowed to fall in, in **seconds**:
 * 2001-09-09 to 2100-01-01. Wide enough for any real event, narrow enough that a
 * millisecond value cannot masquerade as a second one.
 */
export const WA_STATUS_TIMESTAMP_MIN_SECONDS = 1_000_000_000;
export const WA_STATUS_TIMESTAMP_MAX_SECONDS = 4_102_444_800;

/**
 * **When Meta says this status happened.** Its own clock, not the send's.
 *
 * Two bugs are prevented here, and both are the kind that produce plausible
 * numbers rather than errors.
 *
 * **The units.** Meta sends `timestamp` as a **string of unix seconds** —
 * `"1603086346"`. `new Date("1603086346")` is an `Invalid Date`, and
 * `new Date(1603086346)` is the 19th of January 1970. Neither throws. The first
 * reaches `periodOf` as `'Invalid Date'.slice(0, 8) + '01'`, a partition key no
 * partition claims; the second writes a 2020 read receipt into a 1970 partition
 * that also does not exist. The seconds-vs-milliseconds range check above is
 * what refuses both.
 *
 * **The clock.** `delivery_events` is partitioned monthly on `period`, and
 * `period` is pinned by CHECK to the UTC month of `occurred_at`. Slice 11 hit
 * this on the email side and wrote `ses-event-time.ts` for it: an open arriving
 * months after the send must land in the month it *happened*, or a read that
 * prunes to recent partitions never finds it. **A WhatsApp `read` has exactly
 * the same shape** — a message sits unopened for a week and then somebody opens
 * the chat — so dating it from the send would put it in the wrong partition and
 * make it invisible to precisely the window queries that would show engagement
 * rising. The status's own `timestamp` is the only correct source, and there is
 * no `mail.timestamp` fallback to be tempted by.
 *
 * Returns `null` for anything unreadable rather than guessing. The caller falls
 * back to its own clock and says which clock it used, exactly as
 * `sesEventOccurredAt` does — a `received` fallback and a real timestamp are
 * different facts, and a bare `Date` cannot tell them apart.
 */
export function parseMetaStatusTimestamp(raw: unknown): Date | null {
  if (typeof raw === 'number' || typeof raw === 'string') {
    const text = String(raw).trim();
    if (text === '') return null;
    if (/^\d+$/.test(text)) {
      const seconds = Number.parseInt(text, 10);
      // An all-digit value outside the seconds range is a millisecond stamp, a
      // truncation, or something else entirely. Refused rather than rescued:
      // guessing units is how a row lands in a partition nobody queries.
      return Number.isFinite(seconds) ? inRangeOrNull(new Date(seconds * 1_000)) : null;
    }
    // An ISO string is not documented, but it is cheap to accept — and it goes
    // through the *same* range check as the digits above. See `inRangeOrNull`.
    return inRangeOrNull(new Date(text));
  }
  return null;
}

/**
 * The range check, applied to a `Date` rather than to a number, so **both**
 * branches above are held to it.
 *
 * It used to guard only the all-digit branch, and the free-form `new Date(text)`
 * below it returned anything JavaScript did not call `Invalid Date`. Two things
 * got through, and neither looked like an error:
 *
 *   - **a year outside four digits.** `new Date('+275760-09-13')` is a perfectly
 *     valid `Date`, and `periodOf` turns it into `'+275760-01'` —
 *     `toISOString()` prefixes a sign and pads to six digits, so the `slice(0,
 *     8)` that yields `'2026-09-'` for a real date yields something that is not
 *     a date at all. Postgres refuses it (22009) and the whole ingest
 *     transaction aborts, which is how one status came to abandon its batch.
 *     `delivery-period.ts`'s header states that its callers never hand it an
 *     invalid `Date`; that promise needs a date in the *partitioned* range, not
 *     merely one JavaScript can represent, and this is where it is kept;
 *   - **1970.** `'1970-01-19T13:38:06Z'` is exactly what a milliseconds-as-
 *     seconds mistake produces, and it is the mistake the seconds range exists
 *     to catch. Arriving as an ISO string it passed, `waStatusOccurredAt`
 *     reported `source: 'event'` so the "Meta changed the units" warning never
 *     fired, and a status from this month was filed in the default partition
 *     under `period = '1970-01-01'`, where `max(occurred_at)` and every
 *     window-pruned read then disagree with reality for ever.
 *
 * One bound, checked once, whatever spelling the timestamp arrived in.
 */
function inRangeOrNull(parsed: Date): Date | null {
  const seconds = parsed.getTime() / 1_000;
  if (!Number.isFinite(seconds)) return null;
  if (seconds < WA_STATUS_TIMESTAMP_MIN_SECONDS || seconds > WA_STATUS_TIMESTAMP_MAX_SECONDS) {
    return null;
  }
  return parsed;
}

/* -------------------------------------------------------------------------- */
/* The status webhook payload                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The `changes[].field` a status update arrives under. **`messages`**, not
 * `message_status_update` — Meta puts inbound messages and outbound statuses on
 * the *same* field, and tells them apart by which array the value carries.
 *
 * `META_TEMPLATE_STATUS_FIELD` in `./whatsapp.js` is the other one, and the two
 * being different constants in different files is deliberate: they are handled
 * by different services, on the same signed route, and a single
 * `if (field === ...)` covering both is how one of them starts silently
 * swallowing the other's payloads.
 */
export const META_MESSAGE_STATUS_FIELD = 'messages';

/**
 * One entry of a status's `errors` array.
 *
 * `.passthrough()` and every field optional, because this is the object whose
 * shape Meta changes most often — `error_data.details` was added after the
 * webhook shipped, `title` and `message` have swapped roles across versions —
 * and the verbatim bytes are already in `webhook_events`. The only field
 * anything decides on is `code`.
 */
export const metaStatusErrorSchema = z
  .object({
    code: z.union([z.number(), z.string()]).optional(),
    title: z.string().optional(),
    message: z.string().optional(),
    href: z.string().optional(),
    error_data: z.object({ details: z.string().optional() }).passthrough().optional(),
  })
  .passthrough();
export type MetaStatusError = z.infer<typeof metaStatusErrorSchema>;

/**
 * One status object out of `value.statuses[]`.
 *
 * Lenient on purpose, and this is the file's *hostile input* boundary in the
 * same sense `delivery.ts` is: everything here is derived from an HTTP request
 * anyone on the internet can make. The signature check has already run — over
 * the raw bytes, before a field was read, in `meta-webhooks.controller.ts` —
 * and **that verification is what makes this parse safe to be lenient.** Verify
 * first, parse second, act third; nothing below the parse may run on a payload
 * whose verdict was not `valid`.
 *
 * `pricing` and `conversation` are named and then ignored. They are the tenant's
 * own 24-hour-conversation billing with Meta under BYO, they will not agree with
 * a message count, and slice 7 settled that no shape here carries a cost. They
 * are declared so a reader can see they were considered and refused, rather than
 * finding them in the raw payload and assuming nobody looked.
 *
 * **`conversation` is normally absent now**, which changes nothing here and is
 * worth saying before somebody reads its absence as a fault: Meta's change of
 * 8 October 2025 removed the object from message-status webhooks except at a
 * Free Entry Point. Both members were already `.optional()` and read by
 * nothing, so the payload parses exactly as before — this is a declaration of
 * something that used to arrive and mostly does not.
 */
export const metaMessageStatusSchema = z
  .object({
    /** The `wamid` the send returned. Half of {@link waProviderEventId}. */
    id: z.string().optional(),
    status: z.string().optional(),
    /** Unix **seconds**, as a string. See {@link parseMetaStatusTimestamp}. */
    timestamp: z.union([z.string(), z.number()]).optional(),
    /** The recipient's number, digits only and without a `+`. */
    recipient_id: z.union([z.string(), z.number()]).optional(),
    errors: z.array(metaStatusErrorSchema).optional(),
    /** Meta's conversation metering. Read by nothing. See above. */
    conversation: z.object({}).passthrough().optional(),
    /** Meta's billing flags. Read by nothing. See above. */
    pricing: z.object({}).passthrough().optional(),
  })
  .passthrough();
export type MetaMessageStatusPayload = z.infer<typeof metaMessageStatusSchema>;

/**
 * The `value` object of a `messages` change.
 *
 * `statuses` is optional because **the same field carries inbound messages**: a
 * customer replying produces a `messages` change with a `messages` array and no
 * `statuses` array at all. A parser that required `statuses` would reject those
 * payloads, and a webhook that 400s is a webhook Meta backs off from.
 *
 * **`statuses` is `unknown[]` here rather than
 * `z.array(metaMessageStatusSchema)`, and that is the whole point of this
 * schema's shape.** Zod fails an array if any element fails, so declaring the
 * element type here made *one* unreadable status object discard **every sibling
 * status in the same change** — a `delivered` for one message lost because
 * another message's `errors` arrived as an object instead of an array. That is
 * exactly the invisible drop {@link extractMetaMessageStatuses} promises not to
 * do, defeated by the one line in this file that was not lenient. Each element
 * is parsed on its own, with {@link metaMessageStatusSchema}, inside the
 * extractor — so a status this cannot read costs that status and nothing else.
 */
export const metaMessageStatusesValueSchema = z
  .object({
    messaging_product: z.string().optional(),
    metadata: z
      .object({
        display_phone_number: z.string().optional(),
        phone_number_id: z.union([z.string(), z.number()]).optional(),
      })
      .passthrough()
      .optional(),
    statuses: z.array(z.unknown()).optional(),
  })
  .passthrough();
export type MetaMessageStatusesValue = z.infer<typeof metaMessageStatusesValueSchema>;

/** One status update, flattened out of the envelope and already decided. */
export interface MetaMessageStatusUpdate {
  /** The WABA id from the entry. Matched against the tenant's integration. */
  readonly wabaId: string | null;
  /** The business phone number id the status is about. */
  readonly phoneNumberId: string | null;
  /** Meta's `wamid`. `messages.provider_message_id` is looked up by it. */
  readonly messageId: string | null;
  /** `null` for a status word we do not know. Nothing is acted on then. */
  readonly status: WaMessageStatus | null;
  /** Meta's own clock. `null` when unreadable — never silently the send's. */
  readonly occurredAt: Date | null;
  /** The first error code Meta reported, verbatim. `null` when there was none. */
  readonly errorCode: number | string | null;
  /** That code classified. `unclassified` when absent — never `null`. */
  readonly failureClass: WaFailureClass;
  /** Meta's own words about the failure, for a human. `null` when absent. */
  readonly errorMessage: string | null;
  /** The recipient's number as Meta spells it: digits, no `+`. */
  readonly recipientId: string | null;
  /** The dedupe key, composed. `null` only when there was no `wamid`. */
  readonly providerEventId: string | null;
}

/**
 * Every message status in one payload.
 *
 * **Meta batches, and this is where a reader loses events.** One POST carries
 * several entries, each with several changes, each with an array of statuses,
 * and `entry[0].changes[0].value.statuses[0]` — which is what a first
 * implementation reaches for — would drop the rest of a burst silently. Its
 * sibling `extractMetaTemplateStatusUpdates` in `./whatsapp.js` makes the same
 * point and the two are shaped alike on purpose.
 *
 * Changes whose `field` is not {@link META_MESSAGE_STATUS_FIELD} are skipped
 * here and still stored whole by the caller, so a `message_template_status_update`
 * arriving on the same signed route reaches its own service untouched.
 *
 * A status this cannot read is **returned with `status: null`** rather than
 * dropped. The caller writes the raw payload either way, and the difference
 * between "Meta sent something new" and "Meta sent nothing" is the difference
 * between a diagnosable gap and an invisible one.
 *
 * **And it costs that status alone.** Each element of `statuses` is parsed on
 * its own — see {@link metaMessageStatusesValueSchema}, whose `statuses` is
 * `unknown[]` for exactly this. Parsing the array as
 * `z.array(metaMessageStatusSchema)` reads as harmless and is not: Zod fails the
 * whole array when one element fails, so a single malformed status silently
 * discarded every other status in the same change — the invisible drop this
 * paragraph promises does not happen, defeated one line above the promise.
 */
/**
 * What can still be read out of a status object {@link metaMessageStatusSchema}
 * refused. Field by field, so one unreadable field costs one field.
 *
 * **`errors` is deliberately not salvaged, and that asymmetry is the point.**
 * The realistic way a status fails that parse is a changed `errors` shape, and
 * `errors[0].code` is the input to {@link classifyWaFailureCode} — the one
 * decision in this file that can permanently stop messaging a real person. A
 * code read out of a shape nothing validated is a suppression made on a guess.
 * Absent, it classifies as `unclassified`, which suppresses nobody, while the
 * `failed` status itself still releases the credit and marks the message. The
 * verbatim bytes are in `webhook_events` for whoever goes looking.
 */
function salvageMetaStatusFields(element: unknown): MetaMessageStatusPayload {
  if (typeof element !== 'object' || element === null) return {};
  const record = element as Record<string, unknown>;
  const id = record['id'];
  const status = record['status'];
  const timestamp = record['timestamp'];
  const recipientId = record['recipient_id'];
  return {
    ...(typeof id === 'string' ? { id } : {}),
    ...(typeof status === 'string' ? { status } : {}),
    ...(typeof timestamp === 'string' || typeof timestamp === 'number' ? { timestamp } : {}),
    ...(typeof recipientId === 'string' || typeof recipientId === 'number'
      ? { recipient_id: recipientId }
      : {}),
  };
}

export function extractMetaMessageStatuses(payload: MetaWebhookPayload): MetaMessageStatusUpdate[] {
  const updates: MetaMessageStatusUpdate[] = [];

  for (const entry of payload.entry ?? []) {
    const wabaId = entry.id === undefined ? null : String(entry.id);

    for (const change of entry.changes ?? []) {
      if (change.field !== META_MESSAGE_STATUS_FIELD) continue;

      const parsed = metaMessageStatusesValueSchema.safeParse(change.value);
      if (!parsed.success) continue;

      const value = parsed.data;
      const phoneNumberId =
        value.metadata?.phone_number_id === undefined
          ? null
          : String(value.metadata.phone_number_id);

      for (const element of value.statuses ?? []) {
        /*
         * PARSED ONE AT A TIME, AND NEVER SKIPPED WHEN THE PARSE FAILS.
         *
         * `metaMessageStatusSchema` is already about as lenient as a schema can
         * be — every field optional, `.passthrough()` on both levels — so a
         * failure here means Meta sent a shape nothing anticipated. The reply to
         * that is the one this function's contract states: keep the row, with
         * `status: null`, so the ingest records it unprocessed and a person can
         * see that something new arrived. Dropping it would make "Meta sent
         * something we cannot read" indistinguishable from "Meta sent nothing",
         * and the siblings in the same array would go with it.
         *
         * The `id` is salvaged separately for the same reason: even when the
         * rest of the object is unreadable, the wamid is what lets somebody tie
         * the gap to a message.
         */
        const parsedStatus = metaMessageStatusSchema.safeParse(element);
        const raw: MetaMessageStatusPayload = parsedStatus.success
          ? parsedStatus.data
          : salvageMetaStatusFields(element);

        const status = parseWaMessageStatus(raw.status);
        const messageId = raw.id === undefined || raw.id.trim() === '' ? null : raw.id.trim();
        // Only the first error is decided on. Meta sends at most one in
        // practice, and a second one would be a second diagnosis of one
        // failure — the whole array is in `webhook_events` for a human.
        const firstError = raw.errors?.[0];

        updates.push({
          wabaId,
          phoneNumberId,
          messageId,
          status,
          occurredAt: parseMetaStatusTimestamp(raw.timestamp),
          errorCode: firstError?.code ?? null,
          failureClass: classifyWaFailureCode(firstError?.code ?? null),
          errorMessage: firstError?.error_data?.details ?? firstError?.title ?? firstError?.message ?? null,
          recipientId: raw.recipient_id === undefined ? null : String(raw.recipient_id),
          providerEventId:
            messageId === null || status === null ? null : waProviderEventId(messageId, status),
        });
      }
    }
  }

  return updates;
}

/**
 * The whole envelope, re-exported under a name that says what it is here.
 *
 * It is `metaWebhookPayloadSchema` from `./whatsapp.js` and deliberately not a
 * second schema: Meta sends template updates and message statuses through the
 * *same* callback, past the *same* signature check, and two parsers for one
 * envelope is how the two handlers come to disagree about which entry they are
 * looking at.
 */
export const metaStatusWebhookPayloadSchema = metaWebhookPayloadSchema;

/* -------------------------------------------------------------------------- */
/* The read rate — a floor, held by the type                                   */
/* -------------------------------------------------------------------------- */

/**
 * Below this many **delivered** messages, no read figure is produced.
 *
 * The denominator is delivered rather than accepted for the reason
 * `ENGAGEMENT_MIN_VOLUME_FOR_RATE` gives on the email side: nobody reads a
 * message that failed, and dividing reads by accepted folds the failure rate
 * into the read rate.
 *
 * Equal to the engagement floor by coincidence and written out rather than
 * imported, so that moving one does not silently move the other. They answer
 * different questions about different channels.
 */
export const WA_MIN_VOLUME_FOR_READ_RATE = 100;

/**
 * The marker. There is one member and there will not be a second.
 *
 * `'floor'` rather than `'directional'`, and the distinction is not decoration.
 * An email open rate is *directional*: it is wrong in both directions at once —
 * image blocking undercounts, Apple's proxy overcounts — so the true figure sits
 * somewhere either side of it. A WhatsApp read count is a **floor**: every
 * recipient with read receipts off is missing from the numerator and can never
 * appear in it, so the true figure is *at least* this and there is no mechanism
 * pulling the other way. Those are different claims, they license different
 * sentences, and giving them the same word would let one component render both.
 *
 * A literal rather than an enum with `'exact'` beside it, because an exact
 * WhatsApp read rate is not a thing anybody can measure — not Wend, not Meta.
 * An enum with two members is an invitation to write `confidence: 'exact'` on
 * the day somebody decides the caveats are overstated.
 */
export const WA_READ_CONFIDENCE = 'floor' as const;

/**
 * Why a read figure is wrong, and in which direction.
 *
 * Two mechanisms, both real, both always present, and they do not cancel:
 *
 *   - `read_receipts_disabled_undercounts` — **the reason this is a floor.**
 *     WhatsApp lets any user turn read receipts off, and Meta then reports no
 *     `read` status for that person, ever, however carefully they read the
 *     message. There is no signal that they read it and no way to count how many
 *     such recipients a campaign had. The figure is low by an amount nothing can
 *     compute, and the population that switches this on is not random;
 *   - `chat_open_marks_all_read_overcounts` — opening a chat marks **every**
 *     unread message in it read at once. A person opening a thread to answer a
 *     question marks that morning's marketing template read without looking at
 *     it, and a notification preview read on a lock screen marks nothing. Some
 *     of what is counted was not read.
 *
 * The second does not repair the first. They act on different populations, and
 * stating only one of them would be worse than stating neither, because it tells
 * the reader which way to correct — the argument `ENGAGEMENT_CAVEAT_VALUES`
 * makes for always carrying both of the open-rate caveats together.
 */
export const WA_READ_CAVEAT_VALUES = [
  'read_receipts_disabled_undercounts',
  'chat_open_marks_all_read_overcounts',
] as const;
export const waReadCaveatSchema = z.enum(WA_READ_CAVEAT_VALUES);
export type WaReadCaveat = z.infer<typeof waReadCaveatSchema>;

export const WA_READ_CAVEAT_MESSAGES: Readonly<Record<WaReadCaveat, string>> = {
  read_receipts_disabled_undercounts:
    'Anyone who has turned read receipts off in WhatsApp is never reported as having read a message, however carefully they read it. Those readers are missing from this figure and cannot be counted. The real number is higher — by an amount nothing can calculate.',
  chat_open_marks_all_read_overcounts:
    'Opening a WhatsApp chat marks every unread message in it as read at once, so a message somebody scrolled past on the way to another one is counted here as read.',
};

/** The two caveats a read figure always carries. Both, together, always. */
export const WA_READ_RATE_CAVEATS: readonly WaReadCaveat[] = [
  'read_receipts_disabled_undercounts',
  'chat_open_marks_all_read_overcounts',
];

/**
 * A WhatsApp read figure. **Not renderable as an exact rate, by construction.**
 *
 * Compare it with the two shapes it must never be confused with:
 *
 *   - `OverviewRateKpi` in `./overview.js` — what a bounce rate arrives as — has
 *     `threshold` and `band`. This has neither, so it is **not assignable** to
 *     it and cannot reach a component that draws a KPI tile with a red line on
 *     it. And `OverviewRateKpi` lacks `confidence` and `caveats`, so it is not
 *     assignable here either: a bounce rate cannot be quietly rendered through
 *     the read component and inherit its hedging;
 *   - `EngagementRate` in `./engagement.js` — an email open rate — is the closer
 *     call and the one worth spelling out. Its `confidence` is the literal
 *     `'directional'`; this one's is the literal `'floor'`. **The two are
 *     therefore mutually non-assignable**, so a component written to say "this
 *     is approximate, in both directions" cannot be handed a number whose true
 *     value is only ever *higher*, and vice versa. Two channels' caveats are not
 *     interchangeable and the compiler is what says so.
 *
 * There is no `band`, and adding one would be the mistake this shape exists to
 * prevent. A band is a verdict, a verdict needs a threshold, and a floor has no
 * threshold: nothing is wrong at 30% read and nothing is right at 60%, because
 * neither number is the number.
 *
 * `reading` is a {@link import('./overview.js').RateReading} from the same
 * `rateReading` the rest of the product uses, so "not enough volume" is
 * `rate: null` here exactly as it is everywhere else and a component reaching
 * for a number gets nothing to format.
 */
export const waReadRateSchema = z
  .object({
    reading: rateReadingSchema,
    /** Always `'floor'`. See {@link WA_READ_CONFIDENCE}. */
    confidence: z.literal(WA_READ_CONFIDENCE),
    /**
     * Every reason this number is wrong, with its direction. Never empty — a
     * hedged figure displayed with no hedge is an exact figure as far as the
     * reader is concerned.
     */
    caveats: z.array(waReadCaveatSchema).min(1),
  })
  .strict();
export type WaReadRate = z.infer<typeof waReadRateSchema>;

/**
 * The only supported way to build one.
 *
 * There is no `caveats` parameter. Unlike `engagementRate`, which takes the
 * caveat list because an open rate and a click rate carry different ones, there
 * is exactly one read figure on this channel and it always carries both of
 * {@link WA_READ_RATE_CAVEATS} — so the list is not a choice a caller gets to
 * make, and a caller cannot produce an uncaveated read rate by any argument at
 * all. `extraCaveats` is additive only.
 */
export function waReadRate(input: {
  readonly numerator: number;
  readonly denominator: number;
  readonly extraCaveats?: readonly WaReadCaveat[];
  readonly minimum?: number;
}): WaReadRate {
  const reading = rateReading(
    input.numerator,
    input.denominator,
    input.minimum ?? WA_MIN_VOLUME_FOR_READ_RATE,
  );

  return {
    /*
     * MORE READS THAN DELIVERIES IS NOT A RATE OF ANY KIND, SO THERE IS NONE.
     *
     * The two counts are over different populations by design: `read` counts
     * every message with a `read` event, `delivered` counts messages that
     * reached a device, and Meta drops and reorders statuses — so a `read`
     * whose `delivered` never arrived is a real message a real person really
     * read. The numerator is therefore genuinely not a subset of the
     * denominator, and when it exceeds it the denominator is the incomplete
     * one.
     *
     * `rateReading` clamps the ratio at 1, which is the right answer for a
     * bounce rate — a numerator larger than its denominator there is a query
     * bug and 140% on screen sends the reader after the wrong fault. Here the
     * clamp *manufactures a false claim*: this figure is rendered as "at least
     * 38%", and "at least 100%" asserts that every delivered message was read
     * when the two numbers are not over the same set at all. So the reading
     * carries no rate in that case and stays `meaningful: false`, and the raw
     * counts — which are true whatever arrived — are what the screen shows.
     */
    reading:
      reading.numerator > reading.denominator
        ? { ...reading, rate: null, meaningful: false }
        : reading,
    confidence: WA_READ_CONFIDENCE,
    caveats: [...new Set([...WA_READ_RATE_CAVEATS, ...(input.extraCaveats ?? [])])],
  };
}

/* -------------------------------------------------------------------------- */
/* Why messages did not arrive — seven outcomes that stay seven                */
/* -------------------------------------------------------------------------- */

/**
 * The WhatsApp answer to `notDeliveredBreakdownSchema`, and the same rule:
 * **these are never summed into a single "failed" figure, and this schema has no
 * `total` member for that reason.**
 *
 * One number would tell a person only that something is wrong somewhere, and the
 * seven differ in exactly the way that matters — who has to act. A tenant
 * looking at "412 failed" cleans their list. A tenant looking at "1 unreachable,
 * 411 transient" sends the campaign again tomorrow and keeps 411 customers. That
 * is the entire value of this object.
 *
 * The members are {@link WA_FAILURE_CLASS_VALUES} exactly, one field per class,
 * so a class added there without a field here fails to compile.
 */
export const waFailureBreakdownSchema = z
  .object({
    /** Numbers that will never receive. Already suppressed. Fix the list, once. */
    unreachable: z.number().int().min(0),
    /** Rate limits and outages. **Do not clean these off the list.** */
    transient: z.number().int().min(0),
    /** Meta's messaging rules refused this message. The recipient is reachable. */
    policy: z.number().int().min(0),
    /** The template is paused, disabled, missing or mis-parameterised. */
    template: z.number().int().min(0),
    /** The WABA, number or billing is broken. Everything fails until it is fixed. */
    account: z.number().int().min(0),
    /** Wend built a request Meta refused. Ours. */
    request: z.number().int().min(0),
    /** A code we do not recognise. Nobody suppressed. */
    unclassified: z.number().int().min(0),
  })
  .strict();
export type WaFailureBreakdown = z.infer<typeof waFailureBreakdownSchema>;

export const EMPTY_WA_FAILURE_BREAKDOWN: WaFailureBreakdown = {
  unreachable: 0,
  transient: 0,
  policy: 0,
  template: 0,
  account: 0,
  request: 0,
  unclassified: 0,
};

/**
 * Builds the breakdown from a `GROUP BY event_type, <error code>` over
 * `delivery_events`.
 *
 * One builder, so the call sites that want this — campaign results, the
 * overview, a verification — cannot each decide for themselves what an
 * unrecognised code means. It means `unclassified` and it suppresses nobody.
 *
 * Rows whose status is not `failed` are **ignored rather than bucketed**, for
 * the reason `notDeliveredBreakdown` ignores a `delivery` row: a `read` arriving
 * here means the caller's query was wrong, and quietly adding it to a failure
 * count would hide that behind a plausible number.
 *
 * The caller extracts the code out of `payload -> 'errors' -> 0 ->> 'code'`,
 * the same way `notDeliveredBreakdown`'s caller digs `bounce.bounceType` out of
 * an SES payload, and for the same reason: `delivery_events` has no column for a
 * provider's diagnosis and adding one would be a migration per provider.
 */
export function waFailureBreakdown(
  rows: readonly {
    readonly eventType: string;
    readonly errorCode?: number | string | null;
    readonly count: number;
  }[],
): WaFailureBreakdown {
  const counts: WaFailureBreakdown = { ...EMPTY_WA_FAILURE_BREAKDOWN };
  for (const row of rows) {
    const status = parseWaMessageStatus(row.eventType);
    if (status === null || !isWaFailure(status)) continue;
    counts[classifyWaFailureCode(row.errorCode ?? null)] += row.count;
  }
  return counts;
}

/* -------------------------------------------------------------------------- */
/* What a WhatsApp campaign's results screen reads                             */
/* -------------------------------------------------------------------------- */

/**
 * The WhatsApp half of a campaign's results. Computed on read, never stored —
 * the argument `campaignResultsSchema` makes at length, which applies here
 * unchanged because a stored counter drifts on exactly the events this slice is
 * built around: a redelivered status, an out-of-order pair, a reprocessed
 * `webhook_events` row.
 *
 * **There is no `bounced` field and there must not be one.** Decision 1 of this
 * slice: there is no bounce on WhatsApp, and a field called `bounced` holding a
 * failure count would import SES's mental model — hard versus soft, suppress
 * versus retry — onto a channel where the split is seven-way and lives in
 * `notDelivered` instead.
 *
 * `read` is a raw count as well as a rate, and the count is the honest thing at
 * every volume: "312 of 1,204 delivered messages were marked read" is true below
 * the floor, where the percentage is not. The rate is
 * {@link WaReadRate} — a floor, and typed so it cannot be rendered otherwise.
 */
export const waCampaignDeliverySchema = z
  .object({
    campaignId: uuidSchema,
    /** Messages Meta accepted. True on every channel; not the same as delivered. */
    accepted: z.number().int().min(0),
    /** Reached the device. The only status that consumed a credit. */
    delivered: z.number().int().min(0),
    /**
     * Marked read. **A floor** — see {@link WaReadRate}. Carried as a count
     * beside the rate because the count is true at any volume.
     */
    read: z.number().int().min(0),
    /** Did not arrive. Never summed with anything; split by `notDelivered`. */
    failed: z.number().int().min(0),
    /** The seven classes, kept apart. See {@link waFailureBreakdownSchema}. */
    notDelivered: waFailureBreakdownSchema,
    /** Reads over delivered, or `null` below the floor. Never an exact rate. */
    readRate: waReadRateSchema,
    /** The most recent status Meta sent. Null until one arrives. */
    lastStatusAt: isoDateTimeSchema.nullable(),
  })
  .strict();
export type WaCampaignDelivery = z.infer<typeof waCampaignDeliverySchema>;

/**
 * `GET /campaigns/:id/results` for a WhatsApp campaign.
 *
 * An extension of `campaignResultsResponseSchema` rather than a field inside
 * `campaignResultsSchema`, because that schema lives in `./delivery.js` and
 * `./delivery.js` cannot import this file — see this file's header for what
 * happens when it does. The seam is honest anyway: `results` answers "what
 * happened to each message and each person", in vocabulary both channels share,
 * and `whatsapp` is the channel-specific half that is null on an email campaign.
 *
 * `null` rather than an empty breakdown on an email campaign, for the reason
 * `overviewRecentCampaignSchema` puts `null` in `delivered` on an unmeasured
 * channel: zeros are indistinguishable from a measurement that came back empty,
 * and a component cannot accidentally format a number it has not got.
 */
export const waCampaignResultsResponseSchema = campaignResultsResponseSchema.extend({
  whatsapp: waCampaignDeliverySchema.nullable().default(null),
  /**
   * Slice 19: whether delivery reports have ever reached Wend for this workspace,
   * per channel — the fact a campaign with accepted messages and no reported
   * outcome needs, to say "never" flatly instead of "probably". The whole
   * workspace's, not this campaign's: reporting is set up per workspace, and a
   * campaign whose reports never came is usually the symptom rather than the
   * cause. Null-defaulted for an API older than slice 19, which reads as "not
   * known" rather than as "never received".
   */
  deliveryReports: deliveryReportsSchema.nullable().default(null),
});
export type WaCampaignResultsResponse = z.infer<typeof waCampaignResultsResponseSchema>;

/**
 * An empty reading for a campaign that has produced no statuses yet.
 *
 * Exists so the screen's "nothing has come back yet" case is the same object
 * shape as its populated one, rather than a null the component has to branch on
 * a second time. `waFailureBreakdown([])` and `waReadRate({0, 0})` already
 * answer correctly with no rows; this names the whole thing once.
 */
export function emptyWaCampaignDelivery(campaignId: string): WaCampaignDelivery {
  return {
    campaignId,
    accepted: 0,
    delivered: 0,
    read: 0,
    failed: 0,
    notDelivered: { ...EMPTY_WA_FAILURE_BREAKDOWN },
    readRate: waReadRate({ numerator: 0, denominator: 0 }),
    lastStatusAt: null,
  };
}
